<script lang="ts">

import { EventInformationData } from "@/domain/ticket-selection";
import { Component, mixins, Prop } from "vue-facing-decorator";
import { DateTimeUtil, TranslationKey, TranslationService, IconName, PerformanceQASelectors } from "webshop-modules";
import IconComponent from "@/components/atoms/Icon.vue";
import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";

@Component({
    components: {IconComponent}
})
export default class EventPresentationComponent extends mixins(TicketSelectionDisplay, BreakpointHandler) {

    @Prop()
    private readonly eventInformation!: EventInformationData;

    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly IconName: typeof IconName = IconName;
    public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;

    get eventName(): string {
        return this.eventInformation.eventName;
    }

    get eventLocationInformation(): string {
        return this.eventInformation.cityName
            ? `${this.eventInformation.venueName}, ${this.eventInformation.cityName}`
            : `${this.eventInformation.venueName}`;
    }

    get eventFromDate(): string {
        return this.getDateTimeFormatter().format(new Date(this.eventInformation.startDate * 1000));
    }

    get eventFromTime(): string {
        return this.getTimeFormatter().format(new Date(this.eventInformation.startDate * 1000));
    }

    get eventToDate(): string {
        return this.getDateTimeFormatter().format(new Date(this.eventInformation.endDate * 1000));
    }

    get eventToTime(): string {
        return this.getTimeFormatter().format(new Date(this.eventInformation.endDate * 1000));
    }

    get schedules() {
        return this.eventInformation.schedule;
    }

    /** Is exhibition event check */
    public isEventExhibition(eventInformation: EventInformationData): boolean {
        return eventInformation.eventType === 2;
    }

    /** Is voucher event check */
    public isEventVoucher(eventInformation: EventInformationData): boolean {
        return eventInformation.eventType === 3;
    }

    public shouldDisplayOptionalMenuContent() {
        return !this.isMobile || this.isMobileMenuOpen();
    }

    public getDaynameFor(day: string): string {
        return DateTimeUtil.getFullDayNameFor(
            day,
            TranslationService.getLocale(),
            this.eventInformation.timezone
        );
    }

    /** Date display DOM formatter */
    private getDateTimeFormatter(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(TranslationService.getLocale(), {
            timeZone: this.eventInformation.timezone,
            weekday: 'long',
            year: 'numeric',
            day: '2-digit',
            month: '2-digit'
        });
    }

    /** Time display DOM formatter */
    private getTimeFormatter(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(TranslationService.getLocale(), {
            timeZone: this.eventInformation.timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    }

    private isMobileMenuOpen() {
        return this.isMobile && this.displayStateStore.modeSelectionState.seatmap.isMenuExpanded;
    }
}
</script>

<template>
    <div id="event-presentation-component"
         class="o-eventPresentation l-container l-bg-primary l-container-pageheader">
        <div class="m-pageHeader">
            <div class="m-pageHeader__rimL"></div>

            <!-- Event data -->
                <div class="m-pageHeader__container has-sections">
                    <div class="m-pageHeader__section has-sectionBlocks" data-js="seatmap-controls">
                        <div class="o-eventPresentation__data m-pageHeader__sectionBlock"
                             data-js="event-presentation-data-container"
                             :data-qa="PerformanceQASelectors.EVENT_CARD_INFO">
                            <template v-if="isMobile">
                                <template v-if="displayModeState.seatmap.isSeatmapVisible">
                                    <h2 class="m-pageHeader__mobile" v-bind:class="{'-expanded': displayModeState.seatmap.isMenuExpanded}" v-if="eventInformation.parentUrl"><a :href="eventInformation.parentUrl">{{
                                            eventName
                                        }}</a>
                                    </h2>
                                    <h2 class="m-pageHeader__mobile" v-bind:class="{'-expanded': displayModeState.seatmap.isMenuExpanded}" v-else>{{ eventName }}</h2>
                                </template>
                                <template v-else>
                                    <h2 v-if="eventInformation.parentUrl"><a :href="eventInformation.parentUrl">{{
                                            eventName
                                        }}</a>
                                    </h2>
                                    <h2 v-else>{{ eventName }}</h2>
                                </template>
                            </template>
                            <template v-else>
                                <h1 v-if="eventInformation.parentUrl"><a :href="eventInformation.parentUrl">{{
                                        eventName
                                    }}</a>
                                </h1>
                                <h1 v-else>{{ eventName }}</h1>
                            </template>

                            <div class="a-block"
                                 v-if="!isEventVoucher(eventInformation) && shouldDisplayOptionalMenuContent()">
                                <div class="a-block__col">
                                    <IconComponent :icon-id="IconName.Location"/>
                                </div>
                                <div class="a-block__col">
                                    <p>{{ eventLocationInformation }}</p>
                                </div>
                            </div>

                            <div class="a-block" v-if="!isEventVoucher(eventInformation)">
                                <div class="a-block__col" v-show="shouldDisplayOptionalMenuContent()">
                                    <IconComponent :icon-id="IconName.Time"/>
                                </div>
                                <div class="a-block__col">
                                    <p>{{ $filters.capitalize(eventFromDate) }}
                                        <template v-if="!isEventExhibition(eventInformation)">
                                            <span v-translate="{ key: Translation.Date_at_label }"></span> {{
                                                eventFromTime
                                            }}
                                            <span v-if="eventInformation.timezoneOffset"> {{
                                                    eventInformation.startDateTimezoneAbbreviation
                                                }}</span>
                                        </template>

                                        <template v-if="isEventExhibition(eventInformation)">
                                        <span
                                            v-translate="{ key: Translation.Date_to_label, lowercase: true }">to</span>
                                            {{ eventToDate }}
                                        </template>
                                    </p>

                                    <template v-if="schedules !== [] && isEventExhibition(eventInformation)">
                                        <p v-for="(schedule, index) of schedules" :key="'schedule-' + index"
                                           class="o-eventPresentation__schedule">
                                            <template v-for="(interval, index) in schedule['intervals']"
                                                      :key="'interval-' + index">
                                                {{ interval['from'] }} <span
                                                v-translate="{ key: Translation.Date_to_label, lowercase: true }"></span>
                                                {{ interval['to'] }}
                                                <template
                                                    v-if="index !== Object.keys(schedule['intervals']).length - 1">
                                                    |
                                                </template>
                                            </template>
                                            <span v-if="schedule['days']"> - </span>
                                            <span v-for="(day, index) in schedule['days']" :key="'day-' + index">
                                            {{ getDaynameFor(day) }}<template
                                                v-if="index !== Object.keys(schedule['days']).length - 1">, </template>
                                        </span>
                                        </p>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            <div class="m-pageHeader__rimR"></div>
        </div>
    </div>
</template>
